// File: components/cart/cart-item.tsx
"use client"

import type React from "react"
import { useRef, useEffect, useState, useCallback } from "react"
import { Minus, Plus, Trash2, ImageIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn, formatPrice } from "@/lib/utils"
import type { CartItemData } from "./types"
import { ShippingSelector } from "./shipping-selector"
import { useRouter } from "next/navigation"
import { useHeicImage } from "@/hooks/use-heic-image"
import type { ImageRefInput } from "@/lib/storage/public-url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"
import { useOverlay } from "@/components/context/overlay-context"

function gradingColor(grade: string): string {
  const n = Number(grade)
  if (!Number.isFinite(n)) return "bg-muted"
  if (n >= 9) return "bg-emerald-500"
  if (n >= 7) return "bg-amber-500"
  return "bg-orange-500"
}

/**
 * Cart-row thumbnail.
 *
 * SIZE: `CARD_SM`, not one of the square `SQUARE_*` presets. The box is 128 CSS
 * px wide by 160-192 tall and the image is `object-contain`, so a CDN-side
 * square crop would silently cut the top and bottom off every portrait listing
 * photo on the money path. `contain` reproduces today's framing exactly and
 * still replaces a full upload with a 384 px render.
 *
 * ORDER: preset-size the OUTPUT of `useHeicImage`, never the raw prop — for a
 * converted HEIC that output is a `blob:` URL, which `imagePresetUrl` passes
 * through untouched (rewriting it would produce a render URL for a file the
 * CDN has never seen).
 *
 * The comment here used to claim this was "aligned with ListingCard
 * architecture". It was not: `ListingCard` goes through the thumb builder and
 * this row rendered the untouched original (handoff §6).
 *
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §6, §8
 */
function CartItemImage({
  src,
  alt,
  imageLoaded,
  imageRef,
  onLoad,
  onError,
}: {
  /**
   * `listings.image_urls[0]` AS STORED — bucket-relative since phase C.
   * NOT rendered directly: `useHeicImage` is the read funnel and returns an
   * always-resolved `displaySrc`.
   * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
   */
  src: ImageRefInput;
  alt: string;
  imageLoaded: boolean;
  imageRef: React.RefObject<HTMLImageElement | null>;
  onLoad: () => void;
  onError: () => void;
}) {
  const [imageError, setImageError] = useState(false);
  const { displaySrc, isConverting, hasError } = useHeicImage(src);
  // `|| src` dropped: `displaySrc` is ALWAYS resolved and can only be empty
  // when `src` is empty (useHeicImage contract), so the old fallback was dead
  // — and after phase C it would have handed a bucket-relative path to <img>.
  const imageSrc = hasError || imageError
    ? "/placeholder.svg"
    : (imagePresetUrl(displaySrc, CARD_SM) || "/placeholder.svg")
  
  return (
    <>
      {(!imageLoaded || (isConverting && !imageError)) && (
        <Skeleton className="absolute inset-0 z-10" />
      )}
      {imageError || (hasError && !src) ? (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      ) : (
        <img
          ref={imageRef}
          src={imageSrc}
          alt={alt}
          className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          draggable={false}
          style={{ 
            containIntrinsicSize: '128px 128px', 
            backfaceVisibility: 'hidden', 
            transform: 'translateZ(0)', 
            opacity: imageLoaded && !isConverting ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.complete && img.naturalWidth > 0) {
              setImageError(false);
              onLoad();
            }
          }}
          onError={(e) => {
            setImageError(true);
            onError();
          }}
        />
      )}
    </>
  );
}

interface CartItemComponentProps {
  item: CartItemData
  isMobile: boolean
  updateQuantityAction: (id: string, qty: number) => void
  removeItemAction: (id: string) => void
  updateShippingMethodAction: (id: string, shippingId: string) => void
  refreshListingShippingMethodsAction?: (listingId: string) => void | Promise<void>
  activeSwipeItemId: string | null
  setActiveSwipeItemIdAction: (id: string | null) => void
  availability: Record<string, number>
  controlsDisabled?: boolean
  sellerValidationError?: string
  /**
   * Which SURFACE this row is painted on — not a colour switch any more.
   *
   * Was `dark`, and it meant "paint dark literals". Every literal it used to
   * pick is now a token, so what survives is a genuine two-valued choice the
   * tokens cannot make on their own:
   *   - `true`  — the row sits on the overlay panel: the `--sheet*` ladder
   *               (`bg-muted` cards, `border-sheet-handle` rules) and the
   *               rounder sheet geometry.
   *   - `false` — the row sits on the page/desktop-drawer surface: the page
   *               ladder (`bg-card`, `border-border`) and tighter geometry.
   * Both branches are theme-driven; neither is "the dark one".
   *
   * Source of truth is `cart-content.tsx` (`onSheet={isMobile}`): the mobile
   * branch of `cart.tsx` mounts this inside the `DockSheet` panel, the desktop
   * branch inside the hand-rolled drawer. Collapsing the prop would therefore
   * change one of the two live surfaces — it is NOT dead.
   *
   * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §1 (token ladder), §4 (step 2c)
   */
  onSheet?: boolean
  compact?: boolean
}

export function CartItemComponent({
  item,
  isMobile,
  updateQuantityAction,
  removeItemAction,
  updateShippingMethodAction,
  refreshListingShippingMethodsAction,
  activeSwipeItemId,
  setActiveSwipeItemIdAction,
  availability,
  controlsDisabled = false,
  sellerValidationError,
  onSheet = false,
  compact = false,
}: CartItemComponentProps) {
  const router = useRouter()
  const { closeUtility } = useOverlay()
  const startX = useRef(0)
  const startY = useRef(0)
  const startOffset = useRef(0)
  const axisLock = useRef<"none" | "horizontal" | "vertical">("none")
  const isDragging = useRef(false)
  const didDrag = useRef(false)
  const offset = useRef(0)
  const raf = useRef<number | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const deleteButtonRef = useRef<HTMLButtonElement | null>(null)
  const MAX_SWIPE_OFFSET = -80
  const SWIPE_DELETE_GAP = 10
  const SWIPE_BUTTON_WIDTH = Math.abs(MAX_SWIPE_OFFSET) - SWIPE_DELETE_GAP

  const stock = availability[item.listingId] ?? item.maxQuantity
  const isOutOfStock = item.quantity > stock
  // Rail-aware unavailability: seller has no payment rail (PayPal or Stripe).
  // Do NOT derive this from paypalMerchantId — that field is for PayPal SDK
  // rendering only. Server fills availableProviders via getAvailableProvidersForMany.
  // A `pending` row has no server-derived fields yet (see CartItemData.pending),
  // so an empty `availableProviders` means "not answered", not "no rail". It is
  // shown normally but held inert until the reconcile lands — flagging it
  // unavailable would flash a red "seller can't be paid" banner on every add.
  const isUnavailable = !item.pending && (item.availableProviders?.length ?? 0) === 0
  const isCancelled = Boolean(item.chatPrivateCancelledAt)
  const isUnlisted = Boolean(item.isUnlisted)
  const isSellerBanned = Boolean(item.sellerBanned)
  // WS4.6 follow-up: live_rate items have no fixed methods by design.
  const hasNoShippingMethods = !item.pending && item.shippingMode !== "live_rate" && (item.shippingMethods?.length ?? 0) === 0
  const hasSellerError = Boolean(sellerValidationError)
  const isDisabled = isOutOfStock || isUnavailable || hasNoShippingMethods || isCancelled || isUnlisted || isSellerBanned || hasSellerError || controlsDisabled || Boolean(item.pending)

  // Image loading state - aligned with ListingCard architecture
  const [imageLoaded, setImageLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const firstImage = item.images[0] || ""

  useEffect(() => {
    setImageLoaded(!firstImage)
  }, [firstImage])

  const applyOffsetImmediate = useCallback((nextOffset: number, withTransition: boolean) => {
    offset.current = nextOffset
    const contentEl = contentRef.current
    const deleteEl = deleteButtonRef.current
    if (contentEl) {
      contentEl.style.transition = withTransition ? "transform 200ms ease-out" : "none"
      contentEl.style.transform = `translate3d(${nextOffset}px, 0, 0)`
    }
    if (deleteEl) {
      const p = Math.max(0, Math.min(1, (-nextOffset) / Math.abs(MAX_SWIPE_OFFSET)))
      deleteEl.style.transition = withTransition ? "transform 200ms ease-out, opacity 200ms ease-out" : "none"
      deleteEl.style.transform = `translate3d(${(1 - p) * 100}%, 0, 0)`
      deleteEl.style.opacity = String(p)
      deleteEl.style.visibility = p > 0 ? "visible" : "hidden"
      deleteEl.style.pointerEvents = p > 0 ? "auto" : "none"
    }
  }, [])

  const scheduleOffsetUpdate = useCallback((nextOffset: number) => {
    if (raf.current !== null) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      raf.current = null
      applyOffsetImmediate(nextOffset, false)
    })
  }, [applyOffsetImmediate])

  useEffect(() => {
    if (!isMobile) return
    if (isDragging.current) return
    const shouldOpen = activeSwipeItemId === item.id
    const targetOffset = shouldOpen ? MAX_SWIPE_OFFSET : 0
    if (offset.current === targetOffset) return
    applyOffsetImmediate(targetOffset, true)
  }, [activeSwipeItemId, applyOffsetImmediate, isMobile, item.id])

  useEffect(() => {
    if (!isMobile) return
    applyOffsetImmediate(0, false)
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current)
    }
  }, [applyOffsetImmediate, isMobile])

  const handleCardClick = () => {
    if (isMobile) {
      if (didDrag.current || isDragging.current) {
        didDrag.current = false
        return
      }
      if (activeSwipeItemId === item.id) {
        setActiveSwipeItemIdAction(null)
        applyOffsetImmediate(0, true)
        return
      }
    }
    // Fermer le drawer du cart avant de naviguer
    closeUtility()
    router.push(`/listings/${item.listingId}`)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (controlsDisabled) return
    if (!isMobile) return
    if (activeSwipeItemId && activeSwipeItemId !== item.id) {
      setActiveSwipeItemIdAction(null)
    }
    isDragging.current = true
    didDrag.current = false
    axisLock.current = "none"
    applyOffsetImmediate(offset.current, false)
    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
    startOffset.current = offset.current
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (controlsDisabled) return
    if (!isMobile || !isDragging.current) return
    const currentX = e.touches[0].clientX
    const currentY = e.touches[0].clientY
    const dx = currentX - startX.current
    const dy = currentY - startY.current

    if (axisLock.current === "none") {
      const absX = Math.abs(dx)
      const absY = Math.abs(dy)
      if (absX < 6 && absY < 6) return
      axisLock.current = absY > absX ? "vertical" : "horizontal"
    }

    if (axisLock.current !== "horizontal") return

    didDrag.current = true
    e.preventDefault()
    const nextOffset = Math.max(MAX_SWIPE_OFFSET, Math.min(0, startOffset.current + dx))
    scheduleOffsetUpdate(nextOffset)
  }

  const handleTouchEnd = () => {
    if (controlsDisabled) return
    if (!isMobile || !isDragging.current) return
    isDragging.current = false
    if (raf.current !== null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }

    if (axisLock.current === "horizontal") {
      const shouldOpen = offset.current < MAX_SWIPE_OFFSET / 2
      const targetOffset = shouldOpen ? MAX_SWIPE_OFFSET : 0
      applyOffsetImmediate(targetOffset, true)
      setActiveSwipeItemIdAction(shouldOpen ? item.id : null)
    }

    axisLock.current = "none"
  }

  const cardBg = onSheet ? "bg-muted border-sheet-handle" : "bg-card border-border";
  const cardRound = onSheet ? "rounded-2xl" : "rounded-xl";
  const isCompact = compact;

  return (
    <li
      className={cn(
        `cart-item-${item.id} relative cursor-pointer overflow-hidden`,
        isCompact
          ? cn("py-3 border-b last:border-b-0", onSheet ? "border-sheet-handle/50" : "border-border/50")
          : [cardRound, cardBg, "shadow-xs border"],
        "focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-background",
        isDisabled && "opacity-50",
      )}
      onClick={handleCardClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}
    >
      {isMobile && (
        <button
          ref={deleteButtonRef}
          aria-label={`Supprimer ${item.name}`}
          className={cn(
            `delete-button-${item.id} absolute inset-y-0 bg-red-600 text-white flex items-center justify-center`,
            isCompact ? "right-2" : "right-0",
            "focus:outline-hidden focus:ring-2 focus:ring-red-400 focus:ring-offset-2",
          )}
          style={{
            width: `${isCompact ? SWIPE_BUTTON_WIDTH : Math.abs(MAX_SWIPE_OFFSET)}px`,
            transform: "translate3d(100%, 0, 0)",
            opacity: 0,
            visibility: "hidden",
            pointerEvents: "none",
          }}
          onClick={(e) => {
            e.stopPropagation()
            if (controlsDisabled) return
            setActiveSwipeItemIdAction(null)
            removeItemAction(item.id)
          }}
        >
          <Trash2 className="h-5 w-5" />
        </button>
      )}

      {isDisabled && (
        isCompact ? (
          // `bg-background/95`, not a literal: dark `--background` is `#0a0a0a`,
          // byte-for-byte the `bg-neutral-950/95` this used to spell. The scrim
          // and the copy on top of it have to flip TOGETHER — a dark scrim with
          // `text-foreground` on it would be black-on-black in the light theme.
          <div className="absolute inset-0 z-10 flex items-center gap-3 pl-1.5 pr-3 py-3 bg-background/95 rounded-xl">
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate text-[15px] text-foreground">
                {isSellerBanned ? "Banished to the Phantom Zone" : isUnlisted ? "Sorry, this item isn't available anymore" : isCancelled ? "This item was cancelled by the seller" : isOutOfStock ? "This item is out of stock" : hasNoShippingMethods ? "No shipping methods available for this item" : hasSellerError ? "Seller cannot receive payments" : "This item is unavailable"}
              </p>
              <p className="truncate mt-0.5 text-[12px] text-muted-foreground">
                {isSellerBanned ? "This seller violated marketplace rules. Please remove this item to continue." : isUnlisted ? "Please remove it to continue." : isCancelled ? "Please remove it to continue." : isOutOfStock ? "Please remove it to continue." : hasNoShippingMethods ? "Please remove it to continue." : hasSellerError ? (sellerValidationError ?? "Please remove it to continue.") : "Seller cannot accept payments."}
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (!controlsDisabled) removeItemAction(item.id)
              }}
              // Saturated red stays a literal in both branches (spec §7): the
              // pair is a TINT STRENGTH choice per surface, not a theme choice.
              // Revisit by eye at 2f, when the sheet surface turns light.
              className={cn("shrink-0 p-2 rounded-full transition-colors", onSheet ? "bg-red-500/20 text-red-400 hover:bg-red-500/30" : "bg-red-500/10 text-red-500 hover:bg-red-500/20")}
              aria-label="Remove item"
              disabled={controlsDisabled}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ) : (
          // Same scrim contract as the compact branch above: `bg-background/95`
          // is byte-identical to the former `bg-neutral-950/95` in the dark
          // theme, and flips together with the `text-foreground*` copy below.
          <div className="absolute inset-0 bg-background/95 rounded-2xl z-10">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 text-red-500 p-1 hover:bg-red-500 hover:text-white hover:rounded-full focus:outline-hidden focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation()
                if (!controlsDisabled) removeItemAction(item.id)
              }}
              aria-label={`Supprimer ${item.name}`}
              disabled={controlsDisabled}
            >
              <Trash2 className="w-5 h-5" />
            </Button>
            <div className="flex h-full items-center justify-center px-4">
              <div className="text-center space-y-1">
                {isSellerBanned ? (
                  <>
                    <p className="text-foreground text-lg font-bold">Banished to the Phantom Zone</p>
                    <p className="text-foreground/80 text-sm">This seller violated marketplace rules. Please remove this item to continue.</p>
                  </>
                ) : isUnlisted ? (
                  <>
                    <p className="text-foreground text-lg font-bold">Sorry, this item isn't available anymore</p>
                    <p className="text-foreground/80 text-sm">Please remove it to continue.</p>
                  </>
                ) : isCancelled ? (
                  <>
                    <p className="text-foreground text-lg font-bold">This item was cancelled by the seller</p>
                    <p className="text-foreground/80 text-sm">Please remove it to continue.</p>
                  </>
                ) : isOutOfStock ? (
                  <>
                    <p className="text-foreground text-lg font-bold">This item is out of stock</p>
                    <p className="text-foreground/80 text-sm">Please remove it to continue.</p>
                  </>
                ) : hasNoShippingMethods ? (
                  <>
                    <p className="text-foreground text-lg font-bold">No shipping methods available for this item</p>
                    <p className="text-foreground/80 text-sm">Please remove it to continue.</p>
                  </>
                ) : hasSellerError ? (
                  <>
                    <p className="text-foreground text-lg font-bold">Seller cannot receive payments</p>
                    <p className="text-foreground/80 text-sm">{sellerValidationError}</p>
                    <p className="text-foreground/60 text-xs mt-1">Please remove it to continue.</p>
                  </>
                ) : (
                  <>
                    <p className="text-foreground text-lg font-bold">This item is unavailable</p>
                    <p className="text-foreground/80 text-sm">Seller cannot accept payments.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )
      )}

      <div
        ref={contentRef}
        className={cn(
          "relative max-w-full",
          isCompact ? "pl-1.5" : "flex h-auto sm:h-48 md:h-48",
          !isCompact && (onSheet ? "bg-muted" : "bg-card"),
          isMobile && "active:cursor-grabbing",
          isCompact && isMobile && "mr-2",
        )}
        style={{
          transform: "translate3d(0, 0, 0)",
          transition: "transform 200ms ease-out",
          touchAction: "pan-y",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {isCompact ? (
          <>
            <div className="flex gap-2.5 items-start">
              <div className={cn(
                "relative w-12 h-12 shrink-0 self-center rounded-lg overflow-hidden ring-[1.5px] bg-muted",
                onSheet ? "ring-sheet-handle" : "ring-zinc-200 dark:ring-zinc-700",
              )}>
                {!imageLoaded && <Skeleton className="absolute inset-0 z-10" />}
                {firstImage ? (
                  <CartItemImage
                    src={firstImage}
                    alt={item.name}
                    imageLoaded={imageLoaded}
                    imageRef={imageRef}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                  />
                ) : (
                  <div className={cn("absolute inset-0 flex items-center justify-center", onSheet ? "bg-sheet-handle" : "bg-muted")}>
                    <ImageIcon className={cn("h-5 w-5", onSheet ? "text-muted-foreground/70" : "text-muted-foreground")} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-semibold leading-tight text-[15px] text-foreground">{item.name}</span>
                  <span className="shrink-0 font-semibold text-[15px] text-foreground">{formatPrice(item.price * item.quantity, item.currency || "USD")}</span>
                </div>
                <p className="mt-px truncate leading-snug text-[13px] text-muted-foreground">{item.description}</p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  {item.condition && (
                    <span className={cn(
                      "text-xs font-medium px-2.5 py-1 rounded-full",
                      onSheet ? "bg-muted text-foreground" : "bg-foreground/10 text-muted-foreground",
                    )}>
                      {item.condition.charAt(0).toUpperCase() + item.condition.slice(1)}
                    </span>
                  )}
                  {item.grade && item.condition?.toLowerCase() !== "new" && (
                    <span className={cn(
                      "text-xs font-medium px-2.5 py-1 rounded-full",
                      onSheet
                        ? gradingColor(item.grade) === "bg-muted"
                          ? "bg-muted text-muted-foreground"
                          : cn(gradingColor(item.grade), "text-white")
                        : "bg-foreground/10 text-muted-foreground",
                    )}>
                      {item.grade}/10
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 ml-14">
              {item.shippingMode === "live_rate" ? (
                <p className="text-xs italic text-muted-foreground">
                  Live carrier rate at checkout
                </p>
              ) : (
                <ShippingSelector
                  itemId={item.id}
                  shippingMethods={item.shippingMethods}
                  selectedShippingMethod={item.selectedShippingMethod}
                  currencyCode={item.currency || "USD"}
                  onSelectShippingAction={updateShippingMethodAction}
                  onBeforeOpen={() => refreshListingShippingMethodsAction?.(item.listingId)}
                  disabled={isDisabled}
                />
              )}
            </div>
            <div className="mt-1.5 ml-14 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="p-1 text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (controlsDisabled) return;
                    if (item.quantity === 1) {
                      removeItemAction(item.id);
                    } else {
                      updateQuantityAction(item.id, item.quantity - 1);
                    }
                  }}
                  aria-label="Decrease quantity"
                >
                  {item.quantity === 1 ? (
                    // Saturated red: tint strength per surface, not a theme
                    // choice (spec §7). Eyeball at 2f.
                    <Trash2 className={cn("h-4 w-4", onSheet ? "text-red-400" : "text-red-500")} />
                  ) : (
                    <Minus className="h-4 w-4" />
                  )}
                </button>
                <span className="w-6 text-center font-medium text-[13px] text-foreground">{item.quantity}</span>
                <button
                  type="button"
                  className="p-1 text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (controlsDisabled) return;
                    updateQuantityAction(item.id, item.quantity + 1);
                  }}
                  disabled={item.quantity >= stock || isDisabled}
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="relative w-32 sm:w-32 md:w-32 self-stretch min-h-40 shrink-0">
              {!imageLoaded && <Skeleton className="absolute inset-0" />}
              {firstImage ? (
                <CartItemImage
                  src={firstImage}
                  alt={item.name}
                  imageLoaded={imageLoaded}
                  imageRef={imageRef}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(true)}
                />
              ) : (
                <div className={cn("w-full h-full flex items-center justify-center", onSheet ? "bg-sheet-handle" : "bg-muted")}>
                  <ImageIcon className={cn("h-8 w-8", onSheet ? "text-muted-foreground/70" : "text-muted-foreground")} />
                </div>
              )}
            </div>
            <div className="flex-1 p-2 sm:p-2 md:p-2 flex flex-col min-w-0 max-w-full h-full overflow-hidden">
              <div className="flex justify-between items-start mb-2 shrink-0">
                <h3 className="font-semibold text-base line-clamp-1 pr-2 flex-1 min-w-0 text-foreground">{item.name}</h3>
                {item.quantity >= 2 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full shrink-0 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!controlsDisabled) removeItemAction(item.id)
                    }}
                    disabled={isDisabled}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-sm mb-2 line-clamp-1 shrink-0 text-muted-foreground">{item.description}</p>
              <div className={cn("mt-auto pt-2 border-t shrink-0", onSheet && "border-sheet-handle")}>
                <div className="flex flex-wrap gap-2 mb-2">
                  {item.condition && (
                    <span className={cn(
                      "text-xs font-medium px-2.5 py-1 rounded-full shrink-0",
                      onSheet ? "bg-muted text-foreground" : "border border-border",
                    )}>
                      {item.condition.charAt(0).toUpperCase() + item.condition.slice(1)}
                    </span>
                  )}
                  {item.grade && item.condition?.toLowerCase() !== "new" && (
                    <span className={cn(
                      "text-xs font-medium px-2.5 py-1 rounded-full shrink-0",
                      onSheet
                        ? gradingColor(item.grade) === "bg-muted"
                          ? "bg-muted text-muted-foreground"
                          : cn(gradingColor(item.grade), "text-white")
                        : "border border-border",
                    )}>
                      {item.grade}/10
                    </span>
                  )}
                </div>
                <div className="mb-2">
                  {item.shippingMode === "live_rate" ? (
                    <p className="text-xs italic text-muted-foreground">
                      Live carrier rate at checkout
                    </p>
                  ) : (
                    <ShippingSelector
                      itemId={item.id}
                      shippingMethods={item.shippingMethods}
                      selectedShippingMethod={item.selectedShippingMethod}
                      currencyCode={item.currency || "USD"}
                      onSelectShippingAction={updateShippingMethodAction}
                      onBeforeOpen={() => refreshListingShippingMethodsAction?.(item.listingId)}
                      disabled={isDisabled}
                    />
                  )}
                </div>
                <div className="flex justify-between items-center">
                  {/* `border-neutral-600` (#525252) had no exact token; the
                      nearest ladder rung is `--sheet-handle` (#404040) — the
                      one-step approximation the phase sanctions (spec §1). */}
                  <div className={cn("flex items-center border rounded-full overflow-hidden", onSheet && "border-sheet-handle")}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (controlsDisabled) return;
                        if (item.quantity === 1) removeItemAction(item.id);
                        else updateQuantityAction(item.id, item.quantity - 1);
                      }}
                      disabled={isDisabled}
                    >
                      {item.quantity === 1 ? <Trash2 className="h-3 w-3 text-red-500" /> : <Minus className="h-3 w-3" />}
                    </Button>
                    <span className="mx-2 text-sm text-foreground">{item.quantity}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (controlsDisabled) return;
                        updateQuantityAction(item.id, item.quantity + 1)
                      }}
                      disabled={item.quantity >= stock || isDisabled}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <span className="text-xl font-bold text-foreground">{formatPrice(item.price * item.quantity, item.currency || "USD")}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  )
}
